/* ──────────────────────────────────────────────────────────────────────────
 *  GTM adapter — pushes events onto window.dataLayer for Tag Manager.
 *  GTM can then route events to any destination configured in the
 *  GTM container (GA4, Meta Pixel, TikTok, Hotjar, …).
 * ────────────────────────────────────────────────────────────────────────── */

import type {
  AnalyticsAdapter,
  AnalyticsEventName,
  AnalyticsEventMap,
} from "../types";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "";

function push(obj: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
}

export const gtmAdapter: AnalyticsAdapter = {
  name: "gtm",

  init() {
    if (!GTM_ID || typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
  },

  track<E extends AnalyticsEventName>(
    event: E,
    properties: AnalyticsEventMap[E],
  ) {
    if (!GTM_ID) return;
    push({ event, ...properties });
  },

  identify(userId: string, traits?: Record<string, unknown>) {
    if (!GTM_ID) return;
    push({ event: "identify", user_id: userId, ...traits });
  },

  reset() {
    if (!GTM_ID) return;
    push({ event: "reset_identity", user_id: undefined });
  },
};
